import React, { useState, useEffect } from 'react';
import Card from '../../../../shared/components/Card';
import { User } from '../../../../shared/types';
import gameSettingsService from '../../../../shared/services/gameSettingsService';
import usePageTranslation from '../../../../shared/hooks/usePageTranslation';
import ControlsSettingsTab from './settings/ControlsSettingsTab';
import InterfaceSettingsTab from './settings/InterfaceSettingsTab';
import NotificationsSettingsTab from './settings/NotificationsSettingsTab';
import AccountSettingsTab from './settings/AccountSettingsTab';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentUser: User;
  onLogout: () => void;
}

export type SettingsTab = 'controls' | 'interface' | 'notifications' | 'account';

export interface GameSettings {
  keyBindings: Record<string, string>;
  language: string;
  theme: 'dark' | 'light' | 'auto';
  showTooltips: boolean;
  compactMode: boolean;
  emailNotifications: boolean;
  newsletter: boolean;
  inGameNotifications: boolean;
}

function buildDefaultGameSettings(currentUser: User): GameSettings {
  return {
    keyBindings: {},
    language: 'fr',
    theme: 'dark',
    showTooltips: true,
    compactMode: false,
    emailNotifications: currentUser.email_notifications !== false,
    newsletter: currentUser.newsletter === true,
    inGameNotifications: true,
  };
}

function mergeLoadedGameSettings(raw: Record<string, unknown>, base: GameSettings): GameSettings {
  const kb = raw.keyBindings;
  const keyBindings =
    typeof kb === 'object' && kb !== null && !Array.isArray(kb) ? (kb as Record<string, string>) : base.keyBindings;
  const themeRaw = raw.theme;
  const theme =
    themeRaw === 'dark' || themeRaw === 'light' || themeRaw === 'auto' ? themeRaw : base.theme;
  return {
    ...base,
    keyBindings,
    language: typeof raw.language === 'string' ? raw.language : base.language,
    theme,
    showTooltips: typeof raw.showTooltips === 'boolean' ? raw.showTooltips : base.showTooltips,
    compactMode: typeof raw.compactMode === 'boolean' ? raw.compactMode : base.compactMode,
    emailNotifications:
      typeof raw.emailNotifications === 'boolean' ? raw.emailNotifications : base.emailNotifications,
    newsletter: typeof raw.newsletter === 'boolean' ? raw.newsletter : base.newsletter,
    inGameNotifications:
      typeof raw.inGameNotifications === 'boolean' ? raw.inGameNotifications : base.inGameNotifications,
  };
}

const SettingsModal: React.FC<SettingsModalProps> = ({ isOpen, onClose, currentUser, onLogout }) => {
  const { t } = usePageTranslation('game', 'SettingsModal');
  const [activeTab, setActiveTab] = useState<SettingsTab>('interface');
  const [settings, setSettings] = useState<GameSettings>(() => buildDefaultGameSettings(currentUser));

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const result = await gameSettingsService.getSettings();
        if (result.success && result.data) {
          const data = result.data as unknown as Record<string, unknown>;
          setSettings(mergeLoadedGameSettings(data, buildDefaultGameSettings(currentUser)));
        }
      } catch (error) {
        console.error('Erreur lors du chargement des paramètres:', error);
      }
    };

    loadSettings();
  }, [currentUser]);

  const saveSettings = async (newSettings: Partial<GameSettings>) => {
    const updated = { ...settings, ...newSettings };
    setSettings(updated);

    gameSettingsService.updateSettings(newSettings).catch((error) => {
      console.error('Erreur lors de la sauvegarde des paramètres:', error);
    });
  };

  const tabs: { id: SettingsTab; label: string; icon: string }[] = [
    { id: 'controls', label: t('tabs.controls'), icon: '⌨️' },
    { id: 'interface', label: t('tabs.interface'), icon: '🖥️' },
    { id: 'notifications', label: t('tabs.notifications'), icon: '🔔' },
    { id: 'account', label: t('tabs.account'), icon: '👤' },
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <Card className="max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-purple-600 rounded-lg flex items-center justify-center text-2xl">
              ⚙️
            </div>
            <div>
              <h2 className="text-3xl font-bold text-white">{t('title')}</h2>
              <p className="text-sm text-gray-400">{t('subtitle')}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-700 rounded-lg"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex flex-1 gap-6 overflow-hidden">
          <div className="w-48 flex-shrink-0 border-r border-gray-700 pr-4">
            <nav className="space-y-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full text-left px-4 py-3 rounded-lg transition-all flex items-center gap-3 ${
                    activeTab === tab.id
                      ? 'bg-primary-600/30 text-primary-400 border border-primary-500/50'
                      : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
                  }`}
                >
                  <span className="text-xl">{tab.icon}</span>
                  <span className="font-medium">{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>

          <div className="flex-1 overflow-y-auto pr-2">
            {activeTab === 'controls' && (
              <ControlsSettingsTab settings={settings} saveSettings={saveSettings} />
            )}

            {activeTab === 'interface' && (
              <InterfaceSettingsTab settings={settings} saveSettings={saveSettings} />
            )}

            {activeTab === 'notifications' && (
              <NotificationsSettingsTab settings={settings} saveSettings={saveSettings} />
            )}

            {activeTab === 'account' && (
              <AccountSettingsTab currentUser={currentUser} onLogout={onLogout} />
            )}
          </div>
        </div>

        <div className="mt-6 pt-4 border-t border-gray-700 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
          >
            {t('actions.close')}
          </button>
          <button
            onClick={() => {
              const defaultSettings = buildDefaultGameSettings(currentUser);
              setSettings(defaultSettings);
              gameSettingsService.saveSettings(defaultSettings).catch((error) => {
                console.error('Erreur lors de la réinitialisation des paramètres:', error);
              });
            }}
            className="px-6 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg transition-colors"
          >
            {t('actions.reset')}
          </button>
        </div>
      </Card>
    </div>
  );
};

export default SettingsModal;
